import { ComponentRegistryConsumer, withRegistry } from '@bem-react/di';
import b from 'b_';
import cx from 'classnames';
import React from 'react';
import { injectIntl } from 'react-intl';

import DownIcon from './__down-button-icon/paginator__down-button-icon';
import Pages from './__pages/paginator__pages';
import UpIcon from './__up-button-icon/paginator__up-button-icon';
import { PAGINATOR_ORIENTATION, PAGINATOR_THEME } from './paginator.constants';
import { paginationRegistry } from './registry';
import type { Page, PaginatorProps } from './types';
import {
    getSelectedIndex,
    circularShift,
    cnPaginator,
    getCapacityWithDefault,
    groupPagesAroundActivePage,
} from './utils';
import { pageMapper } from './utils/mapping';
import { getInitialScroll, getNextScroll } from './utils/scroll';

import './paginator.scss';

interface State {
    scroll: number;
}

export enum PAGINATOR_BUTTON_TYPES {
    UP = 'up',
    DOWN = 'down',
}

class PaginatorComponent extends React.Component<PaginatorProps, State> {
    static BUTTON_TYPES = PAGINATOR_BUTTON_TYPES;

    static defaultProps = {
        orientation: PAGINATOR_ORIENTATION.VERTICAL,
        theme: PAGINATOR_THEME.DEFAULT,
        showUpButton: true,
        showDownButton: true,
    };

    static getDerivedStateFromProps(nextProps: PaginatorProps, prevState: State) {
        const { pages, capacity } = nextProps;
        const nextPageIndex = getSelectedIndex(pages);
        const { scroll: prevScroll } = prevState;

        const nextScroll = getNextScroll(pages.length, nextPageIndex, prevScroll, getCapacityWithDefault(capacity));

        if (nextScroll !== prevScroll) {
            return { scroll: nextScroll };
        }

        return prevState;
    }

    state = {
        scroll: getInitialScroll(this.props.pages, getCapacityWithDefault(this.props.capacity)),
    };

    goToLastPage = (pages: Page[]) => {
        const page = pages[pages.length - 1];
        page.onClick(pages.length - 1);
    };

    goToFirstPage = (pages: Page[]) => {
        const page = pages[0];
        page.onClick(0);
    };

    getNextPageIndex(selectedIndex: number) {
        const { pages } = this.props;

        const pagesOrdered = circularShift(pages, selectedIndex);
        const nextPage = pagesOrdered.find((page) => !page.disabled);

        if (!nextPage) {
            return undefined;
        }

        return pages.indexOf(nextPage);
    }

    getPrevPageIndex(selectedIndex: number) {
        const { pages } = this.props;

        const pagesOrdered = circularShift(pages, selectedIndex - 1).reverse();
        const nextPage = pagesOrdered.find((page) => !page.disabled);

        if (!nextPage) {
            return undefined;
        }

        return pages.indexOf(nextPage);
    }

    handleUpButtonClick = () => {
        const { pages, onUpButtonClick } = this.props;
        const selectedIndex = getSelectedIndex(pages);

        if (onUpButtonClick) {
            onUpButtonClick();
        }

        const prevPageIndex = this.getPrevPageIndex(selectedIndex);
        if (prevPageIndex !== undefined && pages[prevPageIndex]) {
            pages[prevPageIndex].onClick(prevPageIndex, PAGINATOR_BUTTON_TYPES.UP);
        }
    };

    handleDownButtonClick = () => {
        const { pages, onDownButtonClick } = this.props;
        const selectedIndex = getSelectedIndex(pages);

        if (onDownButtonClick) {
            onDownButtonClick();
        }

        const nextPageIndex = this.getNextPageIndex(selectedIndex);
        if (nextPageIndex !== undefined && pages[nextPageIndex]) {
            pages[nextPageIndex].onClick(nextPageIndex, PAGINATOR_BUTTON_TYPES.DOWN);
        }
    };

    getStyle = (orientation: PaginatorProps['orientation'], pages: PaginatorProps['pages']): React.CSSProperties => {
        if (orientation !== PAGINATOR_ORIENTATION.HORIZONTAL_RESPONSIVE) {
            return {};
        }

        const pagesGroups = groupPagesAroundActivePage(pages);

        return {
            '--l': pagesGroups.left?.length || 0,
            '--r': pagesGroups.right?.length || 0,
        } as React.CSSProperties;
    };

    render() {
        const { pages, mix, orientation, theme, showUpButton, showDownButton, capacity, intl } = this.props;
        const { scroll } = this.state;
        const className = cx(b('paginator', { orientation, theme }), mix);
        const upButtonMix = b('paginator', 'up-button', { orientation });
        const downButtonMix = b('paginator', 'down-button', { orientation });

        const style = this.getStyle(orientation, pages);

        const ariaLabel =
            'aria-label' in this.props ? this.props['aria-label'] : intl.formatMessage({ id: 'a11y.paginator.label' });

        return (
            <ComponentRegistryConsumer id={cnPaginator()}>
                {({ PaginatorButton }) => (
                    <nav aria-label={ariaLabel} className={className} style={style}>
                        {showUpButton && (
                            <PaginatorButton
                                aria-label={intl.formatMessage({ id: 'a11y.paginator.prev' })}
                                clickable
                                mix={upButtonMix}
                                onClick={this.handleUpButtonClick}
                            >
                                <UpIcon />
                            </PaginatorButton>
                        )}
                        <Pages
                            orientation={orientation}
                            pages={pageMapper(
                                pages,
                                scroll,
                                this.goToFirstPage,
                                this.goToLastPage,
                                getCapacityWithDefault(capacity)
                            )}
                        />
                        {showDownButton && (
                            <PaginatorButton
                                aria-label={intl.formatMessage({ id: 'a11y.paginator.next' })}
                                clickable
                                mix={downButtonMix}
                                onClick={this.handleDownButtonClick}
                            >
                                <DownIcon />
                            </PaginatorButton>
                        )}
                    </nav>
                )}
            </ComponentRegistryConsumer>
        );
    }
}

export const Paginator = injectIntl(PaginatorComponent);
export default withRegistry(paginationRegistry)(Paginator);
